/**
 * Whose ledger the workspace page shows.
 *
 * The ledger endpoints are keyed by `companies/:companyId/.../employees/:employeeId`
 * and the backend answers 404 when that employee is not an active member of the
 * company. So the subject must always be a member of the selected company:
 *
 * - Ledger writers (deduction / adjustment / refund rights) pick from the
 *   company's members. Until the member list has loaded there is no subject,
 *   and the viewer's own id is never used as a stand-in: an org admin is often
 *   not a member of the company they manage.
 * - Read-only viewers only see their own ledger, so the page lists only the
 *   companies they belong to (see `ledgerListsAllCompanies`).
 */
export interface LedgerSubjectInput {
  hasWriteAccess: boolean
  /** Employee picked in the dropdown; '' when nothing was picked yet. */
  selectedUserId: string
  /** User ids of the selected company's members; undefined while loading. */
  memberIds: readonly string[] | undefined
  myUserId: string | null
}

export function resolveLedgerSubject({
  hasWriteAccess,
  selectedUserId,
  memberIds,
  myUserId,
}: LedgerSubjectInput): string | null {
  if (!hasWriteAccess) return myUserId || null
  if (!memberIds) return null
  if (selectedUserId && memberIds.includes(selectedUserId)) return selectedUserId
  return memberIds[0] ?? null
}

/**
 * Writers with company read rights manage every company in the org, member or
 * not. Read-only viewers see only their own ledger, which exists only in the
 * companies they belong to.
 */
export function ledgerListsAllCompanies(
  canReadAllCompanies: boolean,
  hasWriteAccess: boolean
): boolean {
  return canReadAllCompanies && hasWriteAccess
}
